import { useMemo, useState } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module13.css'

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`
const today = () => new Date().toISOString().split('T')[0]
const addDays = (n) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().split('T')[0]
}

const STATUS_OPTIONS = [
  ['not_requested', 'Not Requested'],
  ['requested', 'Requested'],
  ['received', 'Received'],
  ['reviewed', 'Reviewed'],
  ['clear', 'Clear'],
  ['issue', 'Issue Found'],
  ['outstanding', 'Outstanding'],
  ['accepted', 'Accepted / Waived'],
]

const CLEAR_STATUSES = new Set(['clear', 'accepted'])
const BLOCKING_STATUSES = new Set(['issue', 'outstanding'])

const CHECKLIST_SECTIONS = [
  {
    id: 'seller_docs',
    label: 'Seller / Property Documents',
    owner: 'Acquisitions',
    items: [
      '3 years tax returns',
      '3 years operating statements / P&L',
      'Current rent roll',
      'Lease files',
      'Security deposit ledger',
      'Utility bills',
      'Insurance history',
      'Service contracts',
      'Property management reports',
      'Maintenance logs',
      'CapEx history',
      'Tenant delinquency / bad debt report',
      'Vacancy report',
      'Eviction history',
      'Open work orders',
    ],
  },
  {
    id: 'municipal',
    label: 'City / Government / Municipal',
    owner: 'City / Counsel',
    items: [
      'City inspection report',
      'Open code violations',
      'Open permits',
      'Expired permits',
      'Fines or tickets',
      'Utility compliance',
      'Zoning confirmation',
      'Certificate of occupancy',
      'Fire/life safety issues',
      'Environmental notices',
      'Pending municipal assessments',
    ],
  },
  {
    id: 'title',
    label: 'Title Due Diligence',
    owner: 'Title / Attorney',
    blocking: true,
    items: [
      'Title commitment received',
      'Title exceptions reviewed',
      'Liens',
      'Unpaid taxes',
      'Unpaid contractor bills',
      'Judgments',
      'Litigation / court cases',
      'HOA or building association dues',
      'Easements',
      'Encroachments',
      'Ownership / entity issues',
      'Payoff statements',
      'Seller loan payoff confirmation',
    ],
  },
]

const INSPECTION_ITEMS = [
  'Maintenance issues',
  'Deferred repairs',
  'Environmental concerns',
  'Roof',
  'Plumbing',
  'Electrical',
  'HVAC',
  'Structural',
  'Unit interiors',
  'Common areas',
  'Parking',
  'Safety issues',
  'Urgent repairs',
]

const CLOSING_CLEARANCE_ITEMS = [
  ['documentsReceived', 'All required documents received'],
  ['documentsReviewed', 'All documents reviewed'],
  ['titleClear', 'No unresolved critical title issues'],
  ['cityClear', 'City/code issues cleared or accepted'],
  ['inspectionClear', 'Inspection issues resolved, credited, or accepted'],
  ['financingClear', 'Financing conditions satisfied'],
  ['insuranceConfirmed', 'Insurance confirmed'],
  ['lenderClear', 'Lender requirements cleared'],
  ['renegotiationFinal', 'Renegotiation finalized or not needed'],
]

function makeId(sectionId, label) {
  return `${sectionId}_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`
}

function flattenChecklist() {
  return CHECKLIST_SECTIONS.flatMap((section) =>
    section.items.map((label, index) => ({
      id: makeId(section.id, label),
      label,
      sectionId: section.id,
      sectionLabel: section.label,
      owner: section.owner,
      blocking: !!section.blocking,
      dueDate: addDays(3 + index),
    }))
  )
}

function makeDefaultRecords(savedRecords = {}) {
  return flattenChecklist().reduce((acc, item) => {
    acc[item.id] = {
      status: 'not_requested',
      owner: item.owner,
      dueDate: item.dueDate,
      notes: '',
      reference: '',
      ...savedRecords[item.id],
    }
    return acc
  }, {})
}

function makeDefaultInspection(savedRecords = {}) {
  return INSPECTION_ITEMS.reduce((acc, label, index) => {
    const id = makeId('inspection', label)
    acc[id] = {
      label,
      status: 'not_requested',
      owner: 'Inspector / PM',
      dueDate: addDays(10 + index),
      estimatedCost: 0,
      responsibleParty: '',
      notes: '',
      reference: '',
      ...savedRecords[id],
    }
    return acc
  }, {})
}

function statusLabel(status) {
  return STATUS_OPTIONS.find(([value]) => value === status)?.[1] || 'Not Requested'
}

function countRecords(records) {
  return Object.values(records).reduce((acc, record) => {
    acc[record.status || 'not_requested'] = (acc[record.status || 'not_requested'] || 0) + 1
    return acc
  }, {})
}

function isClear(record) {
  return CLEAR_STATUSES.has(record?.status)
}

function hasBlocker(record) {
  return BLOCKING_STATUSES.has(record?.status)
}

function ChecklistTable({ items, records, onChange }) {
  return (
    <div className="m13-dd-table-wrap">
      <table className="m13-dd-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Status</th>
            <th>Owner</th>
            <th>Due</th>
            <th>Notes</th>
            <th>Document / Ref</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const record = records[item.id] || {}
            return (
              <tr key={item.id} className={hasBlocker(record) ? 'has-blocker' : isClear(record) ? 'is-clear' : ''}>
                <td>
                  <strong>{item.label}</strong>
                  {item.blocking && <span className="m13-required-chip">Closing Blocker</span>}
                </td>
                <td>
                  <select value={record.status || 'not_requested'} onChange={(e) => onChange(item.id, { status: e.target.value })}>
                    {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </td>
                <td><input value={record.owner || ''} onChange={(e) => onChange(item.id, { owner: e.target.value })} /></td>
                <td><input type="date" value={record.dueDate || ''} onChange={(e) => onChange(item.id, { dueDate: e.target.value })} /></td>
                <td><input value={record.notes || ''} onChange={(e) => onChange(item.id, { notes: e.target.value })} placeholder="Issue, follow-up, or clearance note" /></td>
                <td><input value={record.reference || ''} onChange={(e) => onChange(item.id, { reference: e.target.value })} placeholder="File name, email, link, report #" /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function Module13Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const raw = dealState.rawDealData || {}
  const fin = dealState.extractedFinancials || {}
  const uw = dealState.underwritingMetrics || {}
  const escrow = dealState.escrowDetails || {}
  const saved = dealState.dueDiligence || {}

  const propName = raw.location?.address || dealState.dealName || 'Subject Property'
  const location = raw.location?.city && raw.location?.state ? `${raw.location.city}, ${raw.location.state}` : ''
  const purchasePrice = Number(raw.price) || Number(dealState.loiDocument?.offerPrice) || 0

  const checklistItems = useMemo(() => flattenChecklist(), [])
  const [activeTab, setActiveTab] = useState('checklist')
  const [records, setRecords] = useState(() => makeDefaultRecords(saved.checklistRecords))
  const [inspectionRecords, setInspectionRecords] = useState(() => makeDefaultInspection(saved.inspectionRecords))
  const [renegotiation, setRenegotiation] = useState(() => ({
    originalPurchasePrice: purchasePrice,
    requestedPriceReduction: 0,
    sellerCreditRequest: Number(saved.renegotiation?.sellerCreditRequest) || 0,
    repairCredit: Number(saved.renegotiation?.repairCredit) || 0,
    revisedPurchasePrice: purchasePrice,
    revisedClosingDate: '',
    revisedDueDiligenceDeadline: '',
    reason: '',
    supportingIssues: '',
    status: 'not_needed',
    ...saved.renegotiation,
  }))
  const [clearance, setClearance] = useState(() => ({
    documentsReceived: false,
    documentsReviewed: false,
    titleClear: false,
    cityClear: false,
    inspectionClear: false,
    financingClear: false,
    insuranceConfirmed: false,
    lenderClear: false,
    renegotiationFinal: false,
    ...saved.finalConfirmations,
  }))
  const [saving, setSaving] = useState(false)
  const [completed, setCompleted] = useState(!!saved.finalClearanceConfirmed)

  const counts = useMemo(() => countRecords(records), [records])
  const inspectionCounts = useMemo(() => countRecords(inspectionRecords), [inspectionRecords])
  const clearCount = checklistItems.filter((item) => isClear(records[item.id])).length
  const progress = Math.round((clearCount / checklistItems.length) * 100)

  const blockers = useMemo(() => {
    const checklistBlockers = checklistItems
      .filter((item) => item.blocking ? !isClear(records[item.id]) : hasBlocker(records[item.id]))
      .map((item) => `${item.sectionLabel}: ${item.label} (${statusLabel(records[item.id]?.status)})`)
    const inspectionBlockers = Object.values(inspectionRecords)
      .filter((record) => hasBlocker(record))
      .map((record) => `Inspection: ${record.label} (${statusLabel(record.status)})`)
    const finalBlockers = CLOSING_CLEARANCE_ITEMS
      .filter(([key]) => !clearance[key])
      .map(([, label]) => `Final Clearance: ${label}`)
    return [...checklistBlockers, ...inspectionBlockers, ...finalBlockers]
  }, [checklistItems, records, inspectionRecords, clearance])

  const readyToClose = blockers.length === 0
  const titleClear = CHECKLIST_SECTIONS
    .find((section) => section.id === 'title')
    .items.every((label) => isClear(records[makeId('title', label)]))

  function updateRecord(id, patch) {
    setRecords((current) => ({ ...current, [id]: { ...current[id], ...patch } }))
  }

  function updateInspection(id, patch) {
    setInspectionRecords((current) => ({ ...current, [id]: { ...current[id], ...patch } }))
  }

  function buildPayload(finalClear = false) {
    return {
      checklistRecords: records,
      inspectionRecords,
      renegotiation,
      finalConfirmations: clearance,
      summary: {
        requested: counts.requested || 0,
        received: counts.received || 0,
        reviewed: counts.reviewed || 0,
        issuesFound: (counts.issue || 0) + (inspectionCounts.issue || 0),
        outstanding: (counts.outstanding || 0) + (inspectionCounts.outstanding || 0),
        cleared: clearCount,
        total: checklistItems.length,
        progress,
      },
      financialValidation: clearance.documentsReviewed,
      propertyInspection: clearance.inspectionClear,
      titleIssueResolution: {
        curativeWork: checklistItems
          .filter((item) => item.sectionId === 'title')
          .map((item) => ({ issue: item.label, status: records[item.id]?.status || 'not_requested', note: records[item.id]?.notes || '' })),
        legalFixes: [],
      },
      riskScoring: {
        overallRisk: readyToClose ? 'Low' : blockers.length > 8 ? 'High' : 'Medium',
        issueSeverity: blockers.map((label) => ({ label, risk: label.includes('Title') ? 'high' : 'medium' })),
      },
      blockers,
      readyToClose,
      finalClearanceConfirmed: finalClear,
      savedAt: new Date().toISOString(),
      clearedAt: finalClear ? new Date().toISOString() : saved.clearedAt || null,
    }
  }

  function handleSaveProgress() {
    updateDealState({ dueDiligence: buildPayload(false) })
  }

  function handleComplete() {
    if (!readyToClose) return
    setSaving(true)
    updateDealState({
      dueDiligence: buildPayload(true),
      pipelineStage: Math.max(dealState.pipelineStage || 1, 15),
      pipelineStatus: 'in_progress',
    })
    updateAvailableModules()
    setCompleted(true)
    setTimeout(() => {
      setSaving(false)
      forceNavigateToModule(15)
    }, 600)
  }

  if (completed) {
    return (
      <div className="m13-complete-screen">
        <div className="m13-complete-card">
          <div className="m13-complete-icon">✓</div>
          <h2>Due Diligence Cleared</h2>
          <p>Documents, title, city, inspection, lender, insurance, and renegotiation sign-offs are complete.</p>
          <div className="m13-complete-badge">Ready to Close</div>
          <button className="m13-proceed-btn" onClick={() => forceNavigateToModule(15)}>Go to Financing &amp; Closing</button>
        </div>
      </div>
    )
  }

  return (
    <div className="m13-page">
      <div className="m13-header">
        <div className="m13-header-left">
          <div className="m13-module-badge">Module 13</div>
          <div>
            <h1>Due Diligence &amp; Title Clearance</h1>
            <p className="m13-sub">{propName}{location && ` · ${location}`} · Escrow {escrow.escrowNumber || '-'}</p>
          </div>
        </div>
        <div className="m13-header-right">
          <div className="m13-risk-pill" style={{
            backgroundColor: readyToClose ? '#dcfce7' : '#fee2e2',
            border: `1px solid ${readyToClose ? '#16a34a' : '#dc2626'}`,
            color: readyToClose ? '#166534' : '#991b1b',
          }}>
            {readyToClose ? 'Due Diligence Cleared' : `${blockers.length} Blockers`}
          </div>
          <button className="m13-save-btn" onClick={handleSaveProgress}>Save Progress</button>
        </div>
      </div>

      <div className="m13-progress-row">
        <div className="m13-progress-bar-wrap">
          <div className="m13-progress-bar" style={{ width: `${progress}%`, backgroundColor: readyToClose ? '#16a34a' : '#1d4ed8' }} />
        </div>
        <span className="m13-progress-label">{clearCount} / {checklistItems.length} checklist items clear ({progress}%)</span>
      </div>

      <div className="m13-stats">
        {[
          { label: 'Requested', val: counts.requested || 0 },
          { label: 'Received', val: counts.received || 0 },
          { label: 'Reviewed', val: counts.reviewed || 0 },
          { label: 'Issues Found', val: (counts.issue || 0) + (inspectionCounts.issue || 0), accent: '#dc2626' },
          { label: 'Cleared', val: clearCount, accent: readyToClose ? '#16a34a' : '#1d4ed8' },
        ].map((stat) => (
          <div key={stat.label} className="m13-stat-card">
            <div className="m13-stat-val" style={stat.accent ? { color: stat.accent } : {}}>{stat.val}</div>
            <div className="m13-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {!readyToClose && (
        <div className="m13-blockers-panel">
          <div>
            <h3>Closing Blockers</h3>
            <p>These must be cleared, accepted, or finalized before Financing, Closing &amp; Recording can proceed.</p>
          </div>
          <div className="m13-blocker-list">
            {blockers.slice(0, 8).map((blocker) => <span key={blocker}>{blocker}</span>)}
            {blockers.length > 8 && <span>+{blockers.length - 8} more blockers</span>}
          </div>
        </div>
      )}

      <div className="m13-tabs">
        {[
          ['checklist', 'DD Checklist'],
          ['title', 'Title Issues'],
          ['inspection', 'Inspection Notes'],
          ['clearance', 'Final Clearance'],
        ].map(([id, label]) => (
          <button key={id} className={`m13-tab ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)}>{label}</button>
        ))}
      </div>

      <div className="m13-content">
        {activeTab === 'checklist' && (
          <div className="m13-checklist-panel">
            {CHECKLIST_SECTIONS.map((section) => {
              const items = checklistItems.filter((item) => item.sectionId === section.id)
              const done = items.filter((item) => isClear(records[item.id])).length
              return (
                <div key={section.id} className="m13-dd-section">
                  <div className="m13-section-header">
                    <h3>{section.label}</h3>
                    <p>{done}/{items.length} clear · status, owner, due date, notes, and document reference are tracked per row.</p>
                  </div>
                  <ChecklistTable items={items} records={records} onChange={updateRecord} />
                </div>
              )
            })}
          </div>
        )}

        {activeTab === 'title' && (
          <div className="m13-title-panel">
            <div className="m13-section-header">
              <h3>Title Due Diligence</h3>
              <p>Any unresolved lien, tax, judgment, ownership, payoff, or title exception blocks closing.</p>
            </div>
            <ChecklistTable
              items={checklistItems.filter((item) => item.sectionId === 'title')}
              records={records}
              onChange={updateRecord}
            />
            <div className={`m13-title-standard ${titleClear ? 'is-clear' : ''}`}>
              <h4>{titleClear ? 'Title Clear' : 'Title Not Yet Clear'}</h4>
              <p>{titleClear ? 'All title diligence rows are clear or accepted.' : 'Mark each title issue Clear or Accepted / Waived before closing.'}</p>
            </div>
          </div>
        )}

        {activeTab === 'inspection' && (
          <div className="m13-insp-panel">
            <div className="m13-section-header">
              <h3>Inspection Notes</h3>
              <p>Track maintenance, environmental, repair, and safety issues with cost, responsible party, and status.</p>
            </div>
            <div className="m13-dd-table-wrap">
              <table className="m13-dd-table">
                <thead>
                  <tr>
                    <th>Issue Area</th>
                    <th>Status</th>
                    <th>Cost</th>
                    <th>Responsible Party</th>
                    <th>Due</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(inspectionRecords).map(([id, record]) => (
                    <tr key={id} className={hasBlocker(record) ? 'has-blocker' : isClear(record) ? 'is-clear' : ''}>
                      <td><strong>{record.label}</strong></td>
                      <td>
                        <select value={record.status || 'not_requested'} onChange={(e) => updateInspection(id, { status: e.target.value })}>
                          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </td>
                      <td><input type="number" value={record.estimatedCost || 0} onChange={(e) => updateInspection(id, { estimatedCost: Number(e.target.value) })} /></td>
                      <td><input value={record.responsibleParty || ''} onChange={(e) => updateInspection(id, { responsibleParty: e.target.value })} placeholder="Seller, buyer, PM, contractor" /></td>
                      <td><input type="date" value={record.dueDate || ''} onChange={(e) => updateInspection(id, { dueDate: e.target.value })} /></td>
                      <td><input value={record.notes || ''} onChange={(e) => updateInspection(id, { notes: e.target.value })} placeholder="Finding, quote, or acceptance note" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'clearance' && (
          <div className="m13-clearance-panel">
            <div className="m13-section-header">
              <h3>Final Due Diligence Clearance</h3>
              <p>Every item below must be confirmed before the app unlocks Financing, Closing &amp; Recording.</p>
            </div>
            <div className="m13-clearance-list">
              {CLOSING_CLEARANCE_ITEMS.map(([key, label]) => (
                <label key={key} className={`m13-clearance-row ${clearance[key] ? 'is-clear' : ''}`}>
                  <input type="checkbox" checked={!!clearance[key]} onChange={(e) => setClearance((cur) => ({ ...cur, [key]: e.target.checked }))} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className={`m13-ready-box ${readyToClose ? 'ready' : ''}`}>
              <strong>{readyToClose ? 'Ready to Close' : 'Not Ready to Close'}</strong>
              <span>{readyToClose ? 'Due diligence is cleared and the next module can proceed.' : `${blockers.length} blocker(s) remain.`}</span>
            </div>
          </div>
        )}
      </div>

      <div className="module-bottom-advance">
        <button
          className={`m13-complete-btn ${readyToClose && !saving ? 'ready' : 'disabled'}`}
          onClick={handleComplete}
          disabled={!readyToClose || saving}
        >
          {saving ? 'Confirming Clearance...' : 'Confirm Due Diligence Clearance & Advance to Closing'}
        </button>
      </div>
    </div>
  )
}
